/** DOM events considered "activity" that reset the idle timer (GRA-80). */
const DEFAULT_ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "touchstart",
  "scroll",
] as const;

/** localStorage key used to share the last-activity timestamp across tabs. */
const DEFAULT_STORAGE_KEY = "graftus:lastActivityAt";

/**
 * How often we recompute elapsed idle time from wall-clock timestamps. Also
 * the countdown's resolution during the warning window. Small enough that
 * waking from sleep or a throttled background tab is caught quickly once
 * `check()` runs again (on the next tick, on `visibilitychange`, or on focus).
 */
const DEFAULT_POLL_INTERVAL_MS = 1000;

/**
 * Activity events (pointermove, scroll) can fire hundreds of times a second;
 * only the freshest timestamp matters, so localStorage writes are throttled.
 */
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

export interface IdleTrackerOptions {
  /** Idle duration (ms) after which the user is considered timed out. */
  timeoutMs: number;
  /** How long before timeout `onWarning` starts firing, in ms. */
  warningMs: number;
  /** Called (roughly once per poll) while inside the warning window, with ms remaining. */
  onWarning: (remainingMs: number) => void;
  /** Called once when the idle timeout is reached. */
  onTimeout: () => void;
  activityEvents?: readonly string[];
  storageKey?: string;
  pollIntervalMs?: number;
}

/**
 * Tracks user activity — in this tab and, via a shared localStorage
 * timestamp, every other tab of the same origin — and fires warning/timeout
 * callbacks based on elapsed wall-clock time rather than accumulated
 * `setTimeout` duration. That distinction matters: a laptop that sleeps for
 * 20 minutes and then wakes does not get a fresh 10-minute grace period —
 * the next `check()` (fired on wake via `visibilitychange`/`focus`, or the
 * next poll tick) compares `Date.now()` against the last recorded activity
 * and can time out immediately.
 */
export class IdleTracker {
  private readonly timeoutMs: number;
  private readonly warningMs: number;
  private readonly onWarning: (remainingMs: number) => void;
  private readonly onTimeout: () => void;
  private readonly activityEvents: readonly string[];
  private readonly storageKey: string;
  private readonly pollIntervalMs: number;

  private lastActivityAt = 0;
  private lastActivityWriteAt = 0;
  private timedOut = false;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private started = false;

  constructor(options: IdleTrackerOptions) {
    this.timeoutMs = options.timeoutMs;
    this.warningMs = options.warningMs;
    this.onWarning = options.onWarning;
    this.onTimeout = options.onTimeout;
    this.activityEvents = options.activityEvents ?? DEFAULT_ACTIVITY_EVENTS;
    this.storageKey = options.storageKey ?? DEFAULT_STORAGE_KEY;
    this.pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  }

  start(): void {
    if (this.started || typeof window === "undefined") return;
    this.started = true;
    this.timedOut = false;
    this.lastActivityAt = this.readSharedLastActivity() ?? Date.now();

    for (const event of this.activityEvents) {
      window.addEventListener(event, this.handleActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("storage", this.handleStorage);
    window.addEventListener("focus", this.handleFocus);

    this.intervalId = setInterval(this.check, this.pollIntervalMs);
    this.check();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    for (const event of this.activityEvents) {
      window.removeEventListener(event, this.handleActivity);
    }
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    window.removeEventListener("storage", this.handleStorage);
    window.removeEventListener("focus", this.handleFocus);
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /** Explicitly record activity — used by the "Fortsätt" button in the warning dialog. */
  extend(): void {
    this.recordActivity(Date.now());
    this.check();
  }

  private handleActivity = (): void => {
    const now = Date.now();
    if (now - this.lastActivityWriteAt < ACTIVITY_WRITE_THROTTLE_MS) {
      this.lastActivityAt = now;
      return;
    }
    this.recordActivity(now);
  };

  private recordActivity(now: number): void {
    this.lastActivityAt = now;
    this.lastActivityWriteAt = now;
    this.writeSharedLastActivity(now);
  }

  private handleStorage = (event: StorageEvent): void => {
    if (event.key !== this.storageKey || !event.newValue) return;
    const ts = Number(event.newValue);
    if (Number.isFinite(ts) && ts > this.lastActivityAt) {
      this.lastActivityAt = ts;
      this.check();
    }
  };

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") this.check();
  };

  private handleFocus = (): void => {
    this.check();
  };

  private check = (): void => {
    const now = Date.now();
    const shared = this.readSharedLastActivity();
    if (shared !== null && shared > this.lastActivityAt) {
      this.lastActivityAt = shared;
    }
    const idleMs = now - this.lastActivityAt;

    if (idleMs >= this.timeoutMs) {
      if (!this.timedOut) {
        this.timedOut = true;
        this.onTimeout();
      }
      return;
    }

    const remainingMs = this.timeoutMs - idleMs;
    if (remainingMs <= this.warningMs) {
      this.onWarning(remainingMs);
    }
  };

  private readSharedLastActivity(): number | null {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return null;
      const ts = Number(raw);
      return Number.isFinite(ts) ? ts : null;
    } catch {
      return null;
    }
  }

  private writeSharedLastActivity(now: number): void {
    try {
      window.localStorage.setItem(this.storageKey, String(now));
    } catch {
      // localStorage can throw in private-browsing/storage-full edge cases;
      // cross-tab sync then degrades gracefully to per-tab tracking.
    }
  }
}
